import { Component, OnDestroy, OnInit } from '@angular/core';
import { map, startWith } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  DataService,
  PamlabApiServiceService,
  RouteService,
  ScreenSizeService,
} from '@/shared/services';
import { Observable, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { EntryExitFilters } from '../../../../../server/types';
import { View } from '@/core/interfaces';

interface EntryExitFlowParams {
  cohort: any;
  direction: string;
  hs_gpa_lower: number;
  hs_gpa_upper: number;
  category: string;
  year_range: string;
  entry_exit: string;
}

@Component({
  selector: 'entry-exit-flow-form',
  templateUrl: './entry-exit-flow-form.component.html',
  styleUrls: ['./entry-exit-flow-form.component.css'],
  standalone: false,
})
export class EntryExitFlowFormComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  public entryExitOptions!: Observable<string[]>;
  public filters: EntryExitFilters = {
    cohorts: [],
    colleges: [],
    programs: [],
    departments: [],
  };
  public view!: View;

  // apr form group
  public entryExitFlowForm = new FormGroup({
    cohort: new FormControl(2020, [Validators.required]),
    category: new FormControl('College', [Validators.required]),
    direction: new FormControl('Forward', [Validators.required]),
    entry_exit: new FormControl('', [Validators.required]),
    hs_gpa_lower: new FormControl(0, [Validators.required]),
    hs_gpa_upper: new FormControl(5.04, [Validators.required]),
    year_range: new FormControl('One2Two', [Validators.required]),
  });

  // variable to check if user form submission is same as previous submission
  prevParam: EntryExitFlowParams = {
    year_range: '',
    category: '',
    direction: '',
    hs_gpa_lower: 0,
    hs_gpa_upper: 0,
    entry_exit: '',
    cohort: 0,
  };

  constructor(
    private _dialog: MatDialog,
    private _apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private _routeService: RouteService,
    private screenSizeService: ScreenSizeService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.screenSizeService.screenView$.subscribe((view) => {
        this.view = view;
      }),
    );

    this._apiService.fetchEntryExitFilters().subscribe({
      next: (filters) => {
        this.filters = filters;
        this.entryExitFlowForm.patchValue({ entry_exit: filters.colleges[0] });
        this.entryExitOptions = this.entryExitFlowForm
          .get('entry_exit')!
          .valueChanges.pipe(
            startWith(''),
            map((value) => this._filter(value || '')),
          );
        this.view === 'web' && this.entryExitFlowSubmit();
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  onCategoryChange() {
    // Reset the item control when category changes
    this.entryExitFlowForm
      .get('entry_exit')
      ?.setValue(this._getEntryExitOptions()[0] || '');

    // Update the entryExitOptions observable
    this.entryExitOptions = this.entryExitFlowForm
      .get('entry_exit')!
      .valueChanges.pipe(
        startWith(''),
        map((value) => this._filter(value || '')),
      );
  }

  entryExitFlowSubmit() {
    this._dataService.setPendingData(true);

    // calling _apiService to get student information based on form inputs.
    this._apiService
      .getEntryExitFlow({
        entry_exit: this.entryExitFlowForm.value.entry_exit!,
        category: this.entryExitFlowForm.value.category!,
        direction: this.entryExitFlowForm.value.direction!,
        hs_gpa_lower: this.entryExitFlowForm.value.hs_gpa_lower!,
        hs_gpa_upper: this.entryExitFlowForm.value.hs_gpa_upper!,
        cohort: this.entryExitFlowForm.value.cohort!,
        year_range: this.entryExitFlowForm.value.year_range!,
      })
      .subscribe({
        next: (data) => {
          this._dataService.setData({ data });
          this._dataService.setPendingData(false);
        },
        error: (errorRes: HttpErrorResponse) => {
          if (errorRes.status === 401) {
            this._routeService.navigate(environment['login_url']);
          }
          this._dataService.setData({ error: errorRes.error });
          this._dataService.setPendingData(false);
        },
      });
  }

  // to check if user entered same form value from previous submission
  sameParams() {
    const sameParams =
      this.prevParam &&
      this.prevParam.direction === this.entryExitFlowForm.value.direction &&
      this.prevParam.year_range === this.entryExitFlowForm.value.year_range &&
      this.prevParam.entry_exit === this.entryExitFlowForm.value.entry_exit &&
      this.prevParam.hs_gpa_lower ===
        this.entryExitFlowForm.value.hs_gpa_lower &&
      this.prevParam.hs_gpa_upper ===
        this.entryExitFlowForm.value.hs_gpa_upper &&
      this.prevParam.category === this.entryExitFlowForm.value.category &&
      this.prevParam.cohort === this.entryExitFlowForm.value.cohort;

    this.prevParam = JSON.parse(JSON.stringify(this.entryExitFlowForm.value));

    return sameParams;
  }

  // close popup _dialog after form is submitted
  closeDialog() {
    this._dialog.closeAll();
  }

  isValidForm() {
    return this.entryExitFlowForm.valid;
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    const options: string[] = this._getEntryExitOptions();

    return options.filter((option: string) =>
      option.toLowerCase().includes(filterValue),
    );
  }

  private _getEntryExitOptions(): string[] {
    if (this.entryExitFlowForm.value.category === 'College') {
      return this.filters.colleges;
    }
    if (this.entryExitFlowForm.value.category === 'Program') {
      return this.filters.programs;
    }
    if (this.entryExitFlowForm.value.category === 'Department') {
      return this.filters.departments;
    }
    return [];
  }
}
